/**
 * What Ryn Replay records for a completed XHR's response body (#34222).
 *
 * `xhr.responseText` throws InvalidStateError unless `responseType` is '' or
 * 'text', so reading it on a blob export (every grid "Export to Excel") threw
 * into the host app's global error handler. Only text types read
 * `responseText`, 'json' reads the parsed `xhr.response`, and binary types
 * record a type-and-size placeholder so the request still shows in the replay
 * Network tab. Never throws: a recorder must not break the page it records.
 */
export interface IRynReplayXhrBody {
  /** Text for the ProblemDetails traceId extract (text and json types only). */
  bodyText?: string;
  /** Value for the Network event's responseBody, before truncation. */
  responseBody?: unknown;
}

type XhrBodySource = Pick<
  XMLHttpRequest,
  'responseType' | 'response' | 'responseText'
>;

const binaryPlaceholder = (
  responseType: XMLHttpRequestResponseType,
  response: unknown
): string | undefined => {
  if (response === null || response === undefined) {
    return undefined;
  }
  if (responseType === 'blob') {
    return `[blob ${(response as Blob).size} bytes]`;
  }
  if (responseType === 'arraybuffer') {
    return `[arraybuffer ${(response as ArrayBuffer).byteLength} bytes]`;
  }
  return `[${responseType}]`;
};

export const readRynReplayXhrBody = (xhr: XhrBodySource): IRynReplayXhrBody => {
  try {
    const { responseType } = xhr;
    if (responseType === '' || responseType === 'text') {
      const text = xhr.responseText;
      return typeof text === 'string'
        ? { bodyText: text, responseBody: text }
        : {};
    }
    if (responseType === 'json') {
      const parsed: unknown = xhr.response;
      if (parsed === null || parsed === undefined) {
        return {};
      }
      return { bodyText: JSON.stringify(parsed), responseBody: parsed };
    }
    const placeholder = binaryPlaceholder(responseType, xhr.response);
    return placeholder === undefined ? {} : { responseBody: placeholder };
  } catch {
    return {};
  }
};
